import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Small “i” control. The tip opens on hover or focus, and toggles on click for touch.
 * Bubble is portaled to document.body so sticky headers / overflow do not cover it.
 */
export default function InfoTip({ text, label = "More about this" }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const place = () => {
    const box = btnRef.current?.getBoundingClientRect();
    if (!box) return;
    const width = Math.min(260, window.innerWidth - 16);
    const left = Math.max(8, Math.min(box.left, window.innerWidth - width - 8));
    const below = box.bottom + 6;
    const estimatedHeight = 96;
    const top =
      below + estimatedHeight > window.innerHeight - 8
        ? Math.max(8, box.top - estimatedHeight - 6)
        : below;
    setPos({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    const onScroll = () => place();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  if (text == null || String(text).trim() === "") return null;

  const bubble =
    open && typeof document !== "undefined"
      ? createPortal(
          <span
            className="v2-info-tip__bubble"
            style={{ top: pos.top, left: pos.left }}
            role="tooltip"
          >
            {text}
          </span>,
          document.body
        )
      : null;

  return (
    <span className="v2-info-tip">
      <button
        ref={btnRef}
        type="button"
        className="v2-info-tip__btn"
        aria-label={label}
        aria-expanded={open}
        onMouseEnter={() => {
          place();
          setOpen(true);
        }}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => {
          place();
          setOpen(true);
        }}
        onBlur={() => setOpen(false)}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          // Focus already opens; do not toggle shut on the same activating click.
          place();
          setOpen(true);
        }}
      >
        i
      </button>
      {bubble}
    </span>
  );
}
